import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectItem, Spinner, Tooltip } from "@heroui/react";
import { type ColumnDef } from "@tanstack/react-table";
import { R1DataTable } from "../components/R1DataTable";
import R1FilterBar from "../components/R1FilterBar";
import { useR1DashboardContext } from "../context/R1DashboardProvider";
import { r1Api } from "../services/api";
import { getQuarterFromYYYYMM } from "../utils/quarters";
import { ROW_CONFIG, resolveValue } from "../utils/capitalAccountConfig";
import { ScrollableX } from "../components/ScrollableX";

// Types

type RawTable =
  | "portfolio_positions"
  | "capital_account"
  | "general_information"
  | "fund_overview";

const RAW_TABLE_OPTIONS: { key: RawTable; label: string }[] = [
  { key: "portfolio_positions", label: "Portfolio Positions" },
  { key: "capital_account", label: "Capital Account" },
  { key: "general_information", label: "General Information (Issuers)" },
  { key: "fund_overview", label: "Fund Overview" },
];

// First (sticky/pinned) column per table
const TABLE_FIRST_COL: Partial<Record<RawTable, string>> = {
  portfolio_positions: "raw_name_in_report",
  general_information: "company_name_long",
};

// Key column used for row matching in comparison (may differ from display column)
const TABLE_KEY_COL: Partial<Record<RawTable, string>> = {
  portfolio_positions: "issuer_id",
  general_information: "company_name_long",
};

// Tables rendered as vertical key→value layout (transposed)
const VERTICAL_TABLES = new Set<RawTable>(["capital_account"]);

// Document type badge

function docFileType(fileName: string): "pdf" | "excel" | "other" {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "pdf";
  if (ext === "xlsx" || ext === "xls" || ext === "csv") return "excel";
  return "other";
}

function DocTypeBadge({
  fileName,
  fileType,
}: {
  fileName: string;
  fileType?: string | null;
}) {
  if (fileType === "MASTERSHEET")
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 leading-none">
        MS
      </span>
    );
  const type = docFileType(fileName);
  if (type === "pdf")
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-700 leading-none">
        PDF
      </span>
    );
  if (type === "excel")
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 leading-none">
        TRR
      </span>
    );
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-slate-100 text-slate-500 leading-none">
      DOC
    </span>
  );
}

// Comparison helpers

type ComparisonStatus =
  | "exact"
  | "close"
  | "missing_one"
  | "missing_both"
  | "conflict";

function shortFileName(name: string): string {
  if (name.length <= 18) return name;
  const dotIdx = name.lastIndexOf(".");
  const ext = dotIdx > 0 ? name.slice(dotIdx) : "";
  const base = dotIdx > 0 ? name.slice(0, dotIdx) : name;
  return base.slice(0, 8) + "…" + base.slice(-4) + ext;
}

// Columns where fuzzy similarity would produce false "close" matches
const STRICT_COLS = new Set([
  "id",
  "isin",
  "issuer_id",
  "fund_id",
  "doc_id",
  "r1_document_id",
  "tax_id",
]);

function parseComparableNumber(s: string): number | null {
  const cleaned = s.replace(/,/g, "").replace(/[%x×]/gi, "").trim();
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function diceSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const bigrams = (s: string): string[] => {
    const arr: string[] = [];
    for (let i = 0; i < s.length - 1; i++) arr.push(s[i] + s[i + 1]);
    return arr;
  };
  const bg1 = bigrams(a);
  const bg2Set = new Set(bigrams(b));
  const intersection = bg1.filter((bg) => bg2Set.has(bg)).length;
  return (2 * intersection) / (bg1.length + bg2Set.size);
}

function compareValues(
  key: string,
  v1: unknown,
  v2: unknown,
): ComparisonStatus {
  const isEmpty = (v: unknown) => {
    if (v === null || v === undefined) return true;
    const s = String(v).trim();
    return s === "" || s === "—";
  };
  if (isEmpty(v1) && isEmpty(v2)) return "missing_both";
  if (isEmpty(v1) || isEmpty(v2)) return "missing_one";

  const s1 = String(v1).trim();
  const s2 = String(v2).trim();
  if (s1 === s2) return "exact";

  const n1 = parseComparableNumber(s1);
  const n2 = parseComparableNumber(s2);
  if (n1 !== null && n2 !== null) {
    const base = Math.max(Math.abs(n1), Math.abs(n2));
    if (base === 0) return "exact";
    if (Math.abs(n1 - n2) / base < 0.01) return "close";
    return "conflict";
  }

  if (
    !STRICT_COLS.has(key) &&
    diceSimilarity(s1.toLowerCase(), s2.toLowerCase()) >= 0.9
  )
    return "close";
  return "conflict";
}

const STATUS_DOT_CLASS: Record<ComparisonStatus, string> = {
  exact: "bg-green-500",
  close: "bg-green-300",
  missing_one: "bg-orange-400",
  missing_both: "bg-orange-100 border border-orange-300",
  conflict: "bg-red-500",
};

// Comparison cell

interface CompareInfo {
  primaryName: string;
  otherName: string;
  primaryVal: string;
  otherVal: string;
  status: ComparisonStatus;
}

function ComparisonCell({
  display,
  info,
}: {
  display: string;
  info: CompareInfo | null;
}) {
  if (!info) return <span>{display || "—"}</span>;

  const tooltipContent = (
    <div className="flex flex-col gap-1.5 py-0.5 min-w-[180px] max-w-[300px]">
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide opacity-70">
          {info.primaryName}
        </span>
        <span className="text-[13px] font-semibold">
          {info.primaryVal || "—"}
        </span>
      </div>
      <div className="border-t border-white/20" />
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide opacity-70">
          {info.otherName}
        </span>
        <span className="text-[13px] font-semibold">
          {info.otherVal || "—"}
        </span>
      </div>
    </div>
  );

  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{display || "—"}</span>
      <Tooltip
        content={tooltipContent}
        placement="top"
        delay={0}
        closeDelay={0}
      >
        <span
          className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 cursor-pointer ${STATUS_DOT_CLASS[info.status]}`}
        />
      </Tooltip>
    </span>
  );
}

// Column building

const HIDDEN_COLS = new Set(["r1_document_id"]);

type CompareContext = {
  otherByKey: Map<string, Record<string, unknown>>;
  keyCol: string;
  primaryName: string;
  otherName: string;
};

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function buildColumns(
  rows: Record<string, unknown>[],
  firstCol: string | undefined,
  ctx: CompareContext | null,
): ColumnDef<Record<string, unknown>>[] {
  if (rows.length === 0) return [];

  let keys = Object.keys(rows[0]).filter((k) => !HIDDEN_COLS.has(k));

  // Move firstCol to front
  if (firstCol && keys.includes(firstCol)) {
    keys = [firstCol, ...keys.filter((k) => k !== firstCol)];
  }

  return keys.map((key) => ({
    accessorKey: key,
    header: key,
    size: key === firstCol ? 180 : 130,
    cell: ({ row, getValue }) => {
      const v = getValue();
      const display = formatValue(v);

      if (!ctx) return <span>{display || "—"}</span>;

      const keyVal = String(row.original[ctx.keyCol] ?? "")
        .trim()
        .toLowerCase();
      const otherRow = ctx.otherByKey.get(keyVal);
      if (!otherRow) return <span>{display || "—"}</span>;

      const otherV = otherRow[key];
      const status = compareValues(key, v, otherV);
      const info: CompareInfo = {
        primaryName: ctx.primaryName,
        otherName: ctx.otherName,
        primaryVal: display,
        otherVal: formatValue(otherV),
        status,
      };

      return <ComparisonCell display={display} info={info} />;
    },
  }));
}

// Capital account table

type DocInfo = { id: number; file_name: string; file_type?: string | null };

function RawCapitalAccountTable({
  rows,
  otherRows,
  doc,
  otherDoc,
}: {
  rows: Record<string, unknown>[];
  otherRows: Record<string, unknown>[] | null;
  doc: DocInfo;
  otherDoc: DocInfo | null;
}) {
  const row = rows[0] ?? null;
  const otherRow = (otherDoc && otherRows?.[0]) ?? null;

  if (!row)
    return (
      <div className="flex h-48 items-center justify-center text-sm text-slate-400 italic">
        No data for this document.
      </div>
    );

  const primaryName = shortFileName(doc.file_name);
  const otherName = otherDoc ? shortFileName(otherDoc.file_name) : "";

  return (
    <ScrollableX className="bg-white">
      <table className="text-xs border-collapse" style={{ width: "660px" }}>
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th
              className="sticky left-0 z-20 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 border-r border-gray-200 whitespace-nowrap"
              style={{ minWidth: 260 }}
            >
              Field
            </th>
            <th
              colSpan={2}
              className="px-4 py-2.5 text-center text-[11px] font-bold text-gray-700 border-l border-gray-200 border-r border-gray-200"
              style={{ minWidth: 400 }}
            >
              <div className="flex items-center justify-center gap-1.5">
                <DocTypeBadge
                  fileName={doc.file_name}
                  fileType={doc.file_type}
                />
                <span className="truncate max-w-[300px]">{doc.file_name}</span>
              </div>
            </th>
          </tr>
          <tr className="bg-gray-50 border-b-2 border-gray-200">
            <th
              className="sticky left-0 z-20 bg-gray-50 border-r border-gray-200"
              style={{ minWidth: 260 }}
            />
            <th
              className="px-4 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 border-l border-gray-200"
              style={{ minWidth: 200 }}
            >
              Total Fund (100%)
            </th>
            <th
              className="px-4 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 border-l border-gray-100 border-r border-gray-200"
              style={{ minWidth: 200 }}
            >
              LP Share
            </th>
          </tr>
        </thead>
        <tbody>
          {ROW_CONFIG.map((cfg, idx) => {
            if (cfg.type === "section") {
              return (
                <tr key={idx} className="bg-gray-100">
                  <td
                    className="sticky left-0 z-10 bg-gray-100 border-r border-gray-200 border-b border-b-gray-200"
                    style={{ minWidth: 260 }}
                  />
                  <td
                    colSpan={2}
                    className="border-b border-b-gray-200 border-r border-gray-200 px-4 py-2 text-center text-[11px] font-bold uppercase tracking-widest text-gray-500"
                  >
                    {cfg.label}
                  </td>
                </tr>
              );
            }

            const tf = resolveValue(row, cfg, "tf");
            const lp = resolveValue(row, cfg, "r1");
            const tfOtherDisplay = otherRow
              ? resolveValue(otherRow, cfg, "tf")
              : null;
            const lpOtherDisplay = otherRow
              ? resolveValue(otherRow, cfg, "r1")
              : null;
            // compare raw values (not formatted) to avoid comma-number parsing issues
            const tfStatus = otherRow
              ? compareValues(cfg.tf, row[cfg.tf], otherRow[cfg.tf])
              : null;
            const lpStatus = otherRow
              ? compareValues(cfg.r1, row[cfg.r1], otherRow[cfg.r1])
              : null;

            return (
              <tr
                key={idx}
                className="group border-t border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td
                  className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 px-4 py-1.5 text-gray-700 border-r border-gray-200 whitespace-nowrap transition-colors"
                  style={{ minWidth: 260 }}
                >
                  {cfg.label}
                </td>
                <td
                  className="px-4 py-1.5 text-right tabular-nums text-gray-700 border-l border-gray-200"
                  style={{ minWidth: 200 }}
                >
                  {cfg.isSingle ? (
                    <span className="block text-center font-medium">
                      {tfStatus != null ? (
                        <ComparisonCell
                          display={tf}
                          info={{
                            primaryName,
                            otherName,
                            primaryVal: tf,
                            otherVal: tfOtherDisplay!,
                            status: tfStatus,
                          }}
                        />
                      ) : (
                        tf
                      )}
                    </span>
                  ) : tfStatus != null ? (
                    <ComparisonCell
                      display={tf}
                      info={{
                        primaryName,
                        otherName,
                        primaryVal: tf,
                        otherVal: tfOtherDisplay!,
                        status: tfStatus,
                      }}
                    />
                  ) : (
                    tf
                  )}
                </td>
                <td
                  className="px-4 py-1.5 text-right tabular-nums text-gray-700 border-l border-gray-100 border-r border-gray-200"
                  style={{ minWidth: 200 }}
                >
                  {cfg.isSingle ? null : lpStatus != null ? (
                    <ComparisonCell
                      display={lp}
                      info={{
                        primaryName,
                        otherName,
                        primaryVal: lp,
                        otherVal: lpOtherDisplay!,
                        status: lpStatus,
                      }}
                    />
                  ) : (
                    lp
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollableX>
  );
}

// Fetch helper

function fetchRaw(
  table: RawTable,
  docId: number,
): Promise<{ count: number; data: Record<string, unknown>[] }> {
  if (table === "portfolio_positions")
    return r1Api.getRawPortfolioPositions(docId);
  if (table === "capital_account") return r1Api.getRawCapitalAccount(docId);
  if (table === "general_information")
    return r1Api.getRawGeneralInformation(docId);
  return r1Api.getRawFundOverview(docId);
}

// View

export default function RawDataView() {
  const { selectedFundIds } = useR1DashboardContext();
  const activeFundId = selectedFundIds[0] ?? null;

  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [selectedTable, setSelectedTable] = useState<RawTable>(
    "portfolio_positions",
  );

  // Quarters for the active fund
  const { data: quartersPerFund = {} } = useQuery({
    queryKey: ["r1", "reporting-periods-per-fund", activeFundId],
    queryFn: () =>
      r1Api.getReportingPeriodsPerFund(
        activeFundId
          ? { mode: "ids", ids: [activeFundId] }
          : { mode: "ids", ids: [] },
      ),
    enabled: !!activeFundId,
    staleTime: 60_000,
  });

  const quarters = activeFundId
    ? (quartersPerFund[String(activeFundId)] ?? [])
    : [];

  const effectiveQuarter =
    selectedQuarter && quarters.includes(selectedQuarter)
      ? selectedQuarter
      : (quarters[0] ?? null);

  useEffect(() => {
    setSelectedQuarter(null);
    setSelectedDocId(null);
  }, [activeFundId]);

  // Documents for active fund + quarter
  const { data: docs = [], isLoading: docsLoading } = useQuery({
    queryKey: ["r1", "raw-documents", activeFundId, effectiveQuarter],
    queryFn: () =>
      r1Api.getDocumentsForFundQuarter(activeFundId!, effectiveQuarter!),
    enabled: !!activeFundId && !!effectiveQuarter,
    staleTime: 60_000,
  });

  // Auto-select first doc when list changes
  const effectiveDocId = useMemo(() => {
    if (selectedDocId && docs.some((d) => d.id === selectedDocId))
      return selectedDocId;
    return docs[0]?.id ?? null;
  }, [docs, selectedDocId]);

  // Exclude mastersheet from comparison (it has no raw data rows)
  const comparableDocs = useMemo(
    () => docs.filter((d) => d.file_type !== "MASTERSHEET"),
    [docs],
  );

  // The "other" doc for comparison (only when exactly 2 comparable docs)
  const otherDoc = useMemo(
    () =>
      comparableDocs.length === 2
        ? (comparableDocs.find((d) => d.id !== effectiveDocId) ?? null)
        : null,
    [comparableDocs, effectiveDocId],
  );

  // Primary doc data
  const { data: rawData, isLoading: dataLoading } = useQuery({
    queryKey: ["r1", "raw", selectedTable, effectiveDocId],
    queryFn: () => fetchRaw(selectedTable, effectiveDocId!),
    enabled: !!effectiveDocId,
    staleTime: 60_000,
  });

  // Other doc data (for comparison) — only fetched when a second source exists
  const { data: otherRawData } = useQuery({
    queryKey: ["r1", "raw", selectedTable, otherDoc?.id],
    queryFn: () => fetchRaw(selectedTable, otherDoc!.id),
    enabled: !!otherDoc,
    staleTime: 60_000,
  });

  const sortKey = TABLE_KEY_COL[selectedTable];

  const sortRows = (data: Record<string, unknown>[]) => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      const na = Number(va);
      const nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return String(va).localeCompare(String(vb));
    });
  };

  const rows = useMemo(
    () => sortRows((rawData?.data ?? []) as Record<string, unknown>[]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawData, sortKey],
  );

  const otherRows = useMemo(() => {
    const data = (otherRawData?.data ?? null) as
      | Record<string, unknown>[]
      | null;
    return data ? sortRows(data) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherRawData, sortKey]);

  // Build comparison context for horizontal tables
  const compareCtx = useMemo((): CompareContext | null => {
    if (!otherRows || !otherDoc) return null;
    const keyCol = TABLE_KEY_COL[selectedTable];
    if (!keyCol) return null;

    const primaryDoc = docs.find((d) => d.id === effectiveDocId);
    const map = new Map<string, Record<string, unknown>>();
    for (const r of otherRows) {
      const k = String(r[keyCol] ?? "")
        .trim()
        .toLowerCase();
      if (k) map.set(k, r);
    }

    return {
      otherByKey: map,
      keyCol,
      primaryName: shortFileName(
        primaryDoc?.file_name ?? `Doc ${effectiveDocId}`,
      ),
      otherName: shortFileName(otherDoc.file_name),
    };
  }, [otherRows, otherDoc, selectedTable, docs, effectiveDocId]);

  const columns = useMemo(
    () => buildColumns(rows, TABLE_FIRST_COL[selectedTable], compareCtx),
    [rows, selectedTable, compareCtx],
  );

  const isVertical = VERTICAL_TABLES.has(selectedTable);
  const primaryDoc = docs.find((d) => d.id === effectiveDocId) ?? null;
  const primaryReportingDate = primaryDoc?.reporting_date;

  return (
    <div className="flex flex-col gap-4">
      {/* Row 1 – fund picker + quarter picker */}
      <div className="flex flex-wrap items-center gap-3">
        <R1FilterBar singleSelect hidePeriod />

        {activeFundId && quarters.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-400">Quarter</span>
            <Select
              size="sm"
              selectedKeys={
                effectiveQuarter ? new Set([effectiveQuarter]) : new Set()
              }
              onSelectionChange={(keys) => {
                const q = (Array.from(keys)[0] as string) ?? null;
                setSelectedQuarter(q);
                setSelectedDocId(null);
              }}
              aria-label="Quarter"
              classNames={{
                trigger:
                  "h-8 min-h-8 w-28 shadow-none !rounded-md border-slate-200 text-[12px]",
                value: "text-[12px]",
              }}
            >
              {quarters.map((q) => (
                <SelectItem key={q}>{q}</SelectItem>
              ))}
            </Select>
          </div>
        )}
      </div>

      {!activeFundId ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : !effectiveQuarter ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          No reporting periods available for this fund
        </div>
      ) : (
        <>
          {/* Row 2 – document picker */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Source documents — {effectiveQuarter}
            </span>
            {docsLoading ? (
              <div className="flex h-12 items-center gap-2 text-sm text-slate-400">
                <Spinner size="sm" color="default" />
                Loading documents…
              </div>
            ) : docs.length === 0 ? (
              <div className="text-sm text-slate-400 italic">
                No documents found for this fund and period.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {docs.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedDocId(doc.id)}
                    className={[
                      "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                      effectiveDocId === doc.id
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <DocTypeBadge
                      fileName={doc.file_name}
                      fileType={doc.file_type}
                    />
                    <span className="max-w-[260px] truncate text-[12px] text-slate-700">
                      {doc.file_name}
                    </span>
                    {doc.processing_status && (
                      <span className="ml-1 rounded px-1 py-0.5 text-[9px] font-medium bg-slate-100 text-slate-500">
                        {doc.processing_status}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Row 3 – table picker */}
          {effectiveDocId && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-400">Table</span>
              <Select
                size="sm"
                selectedKeys={new Set([selectedTable])}
                onSelectionChange={(keys) =>
                  setSelectedTable(
                    (Array.from(keys)[0] as RawTable) ?? "portfolio_positions",
                  )
                }
                aria-label="Raw table"
                classNames={{
                  trigger:
                    "h-8 min-h-8 w-64 shadow-none !rounded-md border-slate-200 text-[12px]",
                  value: "text-[12px]",
                }}
              >
                {RAW_TABLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.key}>{opt.label}</SelectItem>
                ))}
              </Select>
            </div>
          )}

          {/* Row 4 – data table */}
          {effectiveDocId && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
              {dataLoading ? (
                <div className="flex h-48 items-center justify-center">
                  <Spinner label="Loading…" color="primary" />
                </div>
              ) : isVertical ? (
                <RawCapitalAccountTable
                  rows={rows}
                  otherRows={otherRows}
                  doc={primaryDoc ?? { id: effectiveDocId, file_name: "" }}
                  otherDoc={otherDoc}
                />
              ) : rows.length === 0 ? (
                <div className="flex h-48 items-center justify-center text-sm text-slate-400 italic">
                  No data for this document / table combination.
                </div>
              ) : (
                <R1DataTable<Record<string, unknown>, unknown>
                  table_name={`r1-raw-${selectedTable}`}
                  columns={columns}
                  data={rows}
                  getRowClassName={() => "hover:!bg-slate-50"}
                  pinnedLeft={
                    TABLE_FIRST_COL[selectedTable]
                      ? [TABLE_FIRST_COL[selectedTable]!]
                      : undefined
                  }
                  noCard
                />
              )}
              <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2">
                <span className="text-xs text-slate-400">
                  {
                    RAW_TABLE_OPTIONS.find((o) => o.key === selectedTable)
                      ?.label
                  }
                  {" · "}
                  doc #{effectiveDocId}
                  {" · "}
                  {primaryReportingDate
                    ? getQuarterFromYYYYMM(primaryReportingDate)
                    : ""}
                </span>
                <span className="text-xs text-slate-400">
                  {rows.length.toLocaleString()} rows
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
